import { Box, Divider, Typography } from '@mui/material';
import React, { useMemo } from 'react';
import moment from 'moment';
import pluralize from '@src/utils/pluralize';
import CommonButton from '@src/new-components/Button';
import ShoppingCartRoundedIcon from '@mui/icons-material/ShoppingCartRounded';
import { LockRounded } from '@mui/icons-material';
import { useHistory } from 'react-router-dom';
import CustomTypography from '@src/new-components/Typography';
import useUserInfo from '@src/hooks/useTtsUser';
import { COLOR } from '@src/styles/color';
import route from '@src/constants/route';
import { useSelector } from 'react-redux';
import LockRoundedIcon from '@mui/icons-material/LockRounded';
import { useTranslation } from 'react-i18next';
import Tooltip from '@src/components/Tooltip';
import {
  getPackageName,
  getParamsExpiringSoon,
  getPathPaymentWhenNotifyLowCredits,
} from '@src/services/package';
import i18n from '@src/languages';
import { delimitNumber } from '@src/utils/number';
import {
  FREE_PACKAGE_CODES,
  PACKAGE_EXPIRED,
  PACKAGE_LEVEL,
  PACKAGE_STUDIO_VERSION,
} from '@src/constants/package';
import { useMappingPackage } from '@src/hooks/useMappingPackage';
import { StyledDetailPackageInfo } from './index.style';

const NewDetailPackageInfo = ({
  getPackageExpired,
  isExpiredPackage,
  remainingCycleCredits,
  remainingTopUpCredits,
  remainingCustomCredits,
  remainingCharacters = 0,
  lockCharacters = 0,
  bonusCharacters = 0,
  formattedNextResetAt,
  isAutoRenewalPackage,
  isPackageExpiringSoon,
  isNotifyLowCredits,
  useInfoCreditsApi,
  apiLimitRemaining,
}) => {
  const history = useHistory();
  const { language } = i18n;
  const { t } = useTranslation();
  const { user } = useSelector((state) => state.auth);
  const { ttsUser } = useUserInfo();
  const { studio } = ttsUser || {};
  const { oneTime } = studio || {};
  const { usingPackage, lastExpiredOrder } = useSelector((state) => state.user);
  const { packages } = useMappingPackage();
  const isLockedCycle = ttsUser?.studio?.cycle?.isLocked;
  const isLockedTopUp = ttsUser?.studio?.topUp?.isLocked;
  const isLockedCustom = ttsUser?.studio?.custom?.isLocked;
  const monthPackage = usingPackage?.expiresIn === PACKAGE_EXPIRED.MONTHLY;
  const totalOldRemainingCharacters =
    remainingCharacters + bonusCharacters || 0;

  const isFreePackage = FREE_PACKAGE_CODES.includes(
    user?.packageCode || 'STUDIO-FREE-V3',
  );
  const userUseOneTimePackage = useMemo(() => {
    const packageVersion = usingPackage?.version || PACKAGE_STUDIO_VERSION.V1;
    return (
      [
        PACKAGE_STUDIO_VERSION.V1,
        PACKAGE_STUDIO_VERSION.V2_1,
        PACKAGE_STUDIO_VERSION.V2,
      ].includes(packageVersion) && !isFreePackage
    );
  }, [usingPackage, isFreePackage]);

  const userUseCustomPackage = useMemo(() => {
    const packageLevel = usingPackage?.level || '';
    return packageLevel === PACKAGE_LEVEL.CUSTOM;
  }, [usingPackage]);

  const getTextPackageExpired = () => {
    let expiryDateText = getPackageExpired();
    if (isExpiredPackage && userUseOneTimePackage && lockCharacters > 0) {
      expiryDateText = `${expiryDateText} (${t('expired').toLowerCase()})`;
    }

    return expiryDateText;
  };

  const packageName = usingPackage.id
    ? getPackageName(
        usingPackage.name,
        usingPackage.code,
        language,
        t,
        true,
        true,
      )
    : getPackageName(
        lastExpiredOrder?.package?.name,
        user?.packageCode,
        language,
        t,
        true,
        true,
      );

  const renderTitleIconLockCredits = () => t('contentTooltipLockCredits');

  const renderIconTooltipLockCredits = () => {
    const titleTooltip = renderTitleIconLockCredits();
    if (
      (isLockedCycle && remainingCycleCredits) ||
      (isLockedCustom && remainingCustomCredits)
    )
      return (
        <Tooltip
          arrow
          className="tootlip"
          title={titleTooltip}
          placement="left"
        >
          <LockRoundedIcon className="icon-lock-cycle" />
        </Tooltip>
      );

    if (isLockedTopUp && remainingTopUpCredits)
      return (
        <Tooltip
          arrow
          className="tootlip"
          title={titleTooltip}
          placement="left"
        >
          <LockRoundedIcon className="icon-lock-cycle" />
        </Tooltip>
      );

    return null;
  };

  const hasOneTimePackage = !userUseOneTimePackage && remainingCharacters > 0;
  const usingCyclePackage =
    !isFreePackage && !userUseOneTimePackage && !userUseCustomPackage;
  const hasOneTimeAndCyclePackage =
    (hasOneTimePackage && usingCyclePackage) ||
    (userUseCustomPackage && hasOneTimePackage);

  const getPriceCyclePackage = () => {
    let total = remainingCycleCredits + remainingTopUpCredits;
    // If use info credits api, total is api limit remaining
    if (useInfoCreditsApi && !userUseCustomPackage) {
      total = apiLimitRemaining;
    }
    if (isExpiredPackage) {
      return `0 ${pluralize(0, t('credit'), language)}`;
    }
    return `${delimitNumber(total, language)} ${pluralize(
      total,
      t('credit'),
      language,
    )}`;
  };

  const getTextButtonWhenNotifyLowCredits = () => {
    if (isFreePackage) return t('upgradeNow');
    if (userUseOneTimePackage) return t('buyNow');
    if (userUseCustomPackage) return t('renewNow');
    return t('buyMoreCharacters');
  };

  // Title Cycle Package V3.3
  const getTitleCyclePackage = () => {
    if (useInfoCreditsApi && !userUseCustomPackage) {
      return t('apiLimitTotal');
    }

    if (monthPackage && !isAutoRenewalPackage) {
      return t('remainingByCharacters');
    }

    return t('remainCycleCharacters');
  };

  const getTextOldPackageWarning = () => {
    if (useInfoCreditsApi) {
      return t('oldPackageV2Warning');
    }
    if (hasOneTimeAndCyclePackage) {
      return t('charactersFromTheOldPlanUsedForAdvancedVoices');
    }

    return t('theseCharactersCanNotBeUsedForAdvancedVoices');
  };

  const getTextButtonBuyNow = () => {
    if (useInfoCreditsApi && !isPackageExpiringSoon) {
      return t('buyPackages');
    }
    if (userUseOneTimePackage) {
      return t('buyNow');
    }

    return t('renewNow');
  };

  const showButtonRenew =
    isFreePackage && !userUseOneTimePackage && useInfoCreditsApi;

  return (
    <div>
      <StyledDetailPackageInfo useInfoCreditsApi={useInfoCreditsApi}>
        <Box className="package-info-container">
          <Box className="package-info">
            <CustomTypography
              variant="body-14-regular"
              color={COLOR.descriptionGray}
            >
              {`${
                hasOneTimeAndCyclePackage ? t('currentPackage') : t('pack')
              }: ${packageName}`}
            </CustomTypography>
            <span style={{ color: COLOR.descriptionUpload }}>|</span>{' '}
            <CustomTypography
              variant="body-12-regular"
              color={
                isPackageExpiringSoon
                  ? COLOR.packageExpiringSoon
                  : COLOR.descriptionUpload
              }
              className={`${isExpiredPackage && 'danger-text'}`}
            >
              {getTextPackageExpired()}
            </CustomTypography>
          </Box>

          {/* User mới đang dùng gói free */}
          {isFreePackage && !userUseOneTimePackage && (
            <div>
              {!useInfoCreditsApi && (
                <Box className="cycle-characters">
                  <CustomTypography
                    variant="body-14-regular"
                    color={COLOR.descriptionUpload}
                    className="cycle-characters-note"
                  >
                    {t('remainingByCharacters')}
                  </CustomTypography>
                  <CustomTypography
                    variant="body-16-bold"
                    color={COLOR.darkGray}
                  >
                    {user?.bonusCharacters?.toLocaleString(language) || 0}{' '}
                    {pluralize(
                      user?.bonusCharacters?.toLocaleString(language),
                      t('credit'),
                      language,
                    )}
                  </CustomTypography>
                </Box>
              )}
              <Box>
                {useInfoCreditsApi ? (
                  <CustomTypography
                    variant="body-12-regular"
                    color={COLOR.descriptionUpload}
                    style={{
                      fontStyle: 'italic',
                    }}
                    dangerouslySetInnerHTML={{
                      __html: t('noteFreePackageAPI'),
                    }}
                  />
                ) : (
                  <CustomTypography
                    variant="body-12-regular"
                    color={COLOR.descriptionUpload}
                    style={{
                      fontStyle: 'italic',
                    }}
                  >
                    {t('resetAtTheBeginningOfTheDay')} {formattedNextResetAt}
                  </CustomTypography>
                )}
              </Box>
            </div>
          )}

          {/* Đang dùng gói chu kỳ */}
          {usingCyclePackage && (
            <div>
              <Box className="cycle-characters">
                <CustomTypography
                  variant="body-14-regular"
                  color={COLOR.descriptionUpload}
                  className="cycle-characters-note"
                >
                  {getTitleCyclePackage()}
                </CustomTypography>
                <CustomTypography
                  style={{ display: 'flex', alignItems: 'center', gap: '4px' }}
                  variant="body-16-bold"
                  color={isExpiredPackage ? COLOR.textRejected : COLOR.darkGray}
                >
                  {getPriceCyclePackage()}
                  {renderIconTooltipLockCredits()}
                </CustomTypography>
              </Box>
              {!isExpiredPackage && (!monthPackage || isAutoRenewalPackage) && (
                <Box>
                  <CustomTypography
                    variant="body-12-regular"
                    color={COLOR.descriptionUpload}
                    style={{
                      fontStyle: 'italic',
                    }}
                  >
                    {t('resetAtTheBeginningOfTheDay')} {formattedNextResetAt}
                  </CustomTypography>
                </Box>
              )}
            </div>
          )}

          {/* if user use custom package */}
          {userUseCustomPackage && (
            <Box className="cycle-characters">
              <CustomTypography
                variant="body-14-regular"
                color={COLOR.descriptionUpload}
                className="cycle-characters-note"
              >
                {useInfoCreditsApi
                  ? t('remainingAPILimit')
                  : t('remainingByCharacters')}
              </CustomTypography>
              <div
                style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
              >
                <CustomTypography
                  variant="body-16-bold"
                  color={isExpiredPackage ? COLOR.textRejected : COLOR.darkGray}
                  className="remain-custom-characters"
                >
                  {`${delimitNumber(
                    remainingCustomCredits,
                    language,
                  )} ${pluralize(
                    remainingCustomCredits,
                    t('credit'),
                    language,
                  )}`}
                </CustomTypography>
                {renderIconTooltipLockCredits()}
              </div>
            </Box>
          )}

          {/* if user have V3 package and remainingCharacter */}
          {/* if user have old package and valid remainingChracters */}
          {((!userUseOneTimePackage &&
            remainingCharacters > 0 &&
            !isExpiredPackage) ||
            (userUseOneTimePackage && !isExpiredPackage)) && (
            <>
              {hasOneTimeAndCyclePackage && <Divider />}
              {(usingCyclePackage || userUseCustomPackage) && (
                <Box className="package-info">
                  <CustomTypography
                    variant="body-14-regular"
                    color={COLOR.descriptionGray}
                  >
                    {`${t('oldPackageV2')} ${getPackageName(
                      null,
                      oneTime?.packageCode,
                      language,
                      t,
                      true,
                      true,
                    )}`}
                  </CustomTypography>
                  <CustomTypography
                    variant="body-12-regular"
                    color={COLOR.descriptionUpload}
                    className={`${isExpiredPackage && 'danger-text'}`}
                  >
                    <span style={{ marginRight: '8px' }}>|</span>{' '}
                    {getTextPackageExpired()}
                  </CustomTypography>
                </Box>
              )}

              <div>
                {!useInfoCreditsApi && (
                  <Box className="old-characters">
                    <CustomTypography
                      variant="body-14-regular"
                      color={COLOR.descriptionUpload}
                    >
                      {t('remainingByCharacters')}
                    </CustomTypography>
                    <CustomTypography
                      variant="body-16-bold"
                      color={COLOR.darkGray}
                    >
                      {`${delimitNumber(
                        totalOldRemainingCharacters,
                        language,
                      )} ${pluralize(
                        totalOldRemainingCharacters,
                        t('credit'),
                        language,
                      )}`}
                    </CustomTypography>
                  </Box>
                )}
                <Box>
                  <CustomTypography
                    variant="body-12-regular"
                    color={COLOR.descriptionUpload}
                    style={{
                      fontStyle: 'italic',
                    }}
                  >
                    {getTextOldPackageWarning()}
                  </CustomTypography>
                </Box>
              </div>
            </>
          )}

          {/* if user have old package and expired */}
          {userUseOneTimePackage && isExpiredPackage && (
            <>
              <Box className="old-characters">
                <Typography className="remain-old-characters">
                  {`${delimitNumber(lockCharacters, language)} ${pluralize(
                    lockCharacters,
                    t('credit'),
                    language,
                  )}`}
                </Typography>
                <Typography className="old-characters-note">
                  {lockCharacters > 0 && (
                    <LockRounded sx={{ fontSize: 16 }} className="lock-icon" />
                  )}
                </Typography>
              </Box>

              {lockCharacters > 0 && (
                <Typography className="preserver-date">{`${t(
                  'preservedCharactersDateV2',
                )}: ${moment(user?.preservationExpiryDate).format(
                  'DD/MM/YYYY - HH:mm',
                )}`}</Typography>
              )}
            </>
          )}
        </Box>
      </StyledDetailPackageInfo>
      {(isPackageExpiringSoon || showButtonRenew) && (
        <CommonButton
          customStyle={{
            width: '100%',
            height: '40px',
            padding: '8px 16px',
            borderRadius: '0px 0px 8px 8px',
          }}
          onClick={() => {
            const paramsExpiringSoon = getParamsExpiringSoon(
              usingPackage,
              packages,
            );

            history.push(
              userUseOneTimePackage
                ? `${route.PAYMENT}`
                : `${route.PAYMENT}?${paramsExpiringSoon}`,
            );
          }}
          startIcon={
            <ShoppingCartRoundedIcon
              sx={{ fontSize: 24, color: COLOR.navyBlue }}
            />
          }
          variant="contained"
          textProps={{
            variantText: 'body-14-semibold',
            color: COLOR.darkBlue,
          }}
        >
          {getTextButtonBuyNow()}
        </CommonButton>
      )}
      {isNotifyLowCredits && !isPackageExpiringSoon && (
        <CommonButton
          customStyle={{
            width: '100%',
            height: '40px',
            padding: '8px 16px',
            borderRadius: '0px 0px 8px 8px',
          }}
          onClick={() => {
            const pathPayment = getPathPaymentWhenNotifyLowCredits(
              usingPackage,
              packages,
            );

            history.push(pathPayment);
          }}
          startIcon={
            <ShoppingCartRoundedIcon
              sx={{ fontSize: 24, color: COLOR.navyBlue }}
            />
          }
          variant="contained"
          textProps={{ variantText: 'body-14-semibold', color: COLOR.darkBlue }}
        >
          {getTextButtonWhenNotifyLowCredits()}
        </CommonButton>
      )}
    </div>
  );
};

export default NewDetailPackageInfo;
